/**
 * @public
 */
export type CssLengthUnit = 'em' | 'ex' | 'ch' | 'rem' | 'vh' | 'vw' | 'vmin' | 'vmax' | 'svw' | 'svh' | 'dvw' | 'dvh' | 'lvw' | 'lvh' | 'px' | 'mm' | 'cm' | 'in' | 'pt' | 'pc' | 'mozmm';

/**
 * @public
 */
export type CssNumber = `${number}${CssLengthUnit}` | number;

/**
 * @public
 */
export type CssLength = `${number}${CssLengthUnit | '%'}` | CssNumber | 'auto';

export namespace CssLength {

    /**
     * Determines whether the given value is a valid CSS length.
     *
     * @public
     * @param value - The value to check.
     * @returns `true` if the value is a valid CSS length; otherwise, `false`.
     */
    export function isCssLength(value: unknown): value is CssLength {
        if (typeof value === 'number' || value === 'auto') {
            return true;
        }

        if (typeof value !== 'string') {
            return false;
        }

        if (value.trim() === '') {
            return false;
        }

        const lengthRegex = /^-?\d+(\.\d+)?(em|ex|ch|rem|vh|vw|vmin|vmax|svw|svh|dvw|dvh|lvw|lvh|px|mm|cm|in|pt|pc|mozmm|%)/;

        return lengthRegex.test(value);
    }

    /**
     * Determines whether the given value is 'auto'.
     *
     * @public
     * @param value - The value to check.
     * @returns `true` if the value is 'auto'; otherwise, `false`.
     */
    export function isAuto(value: unknown): value is Extract<CssLength, 'auto'> {
        return value === 'auto';
    }

    /**
     * Determines whether the given value is a percentage CSS length.
     *
     * @public
     * @param value - The value to check.
     * @returns `true` if the value is a percentage; otherwise, `false`.
     */
    export function isPercentage(value: unknown): value is `${number}%` {
        if (typeof value !== 'string') {
            return false;
        }

        if (value.trim() === '') {
            return false;
        }

        const percentageRegex = /^-?\d+(\.\d+)?%/;

        return percentageRegex.test(value);
    }

    /**
     * Determines whether the given value is a valid CSS length with units (excluding percentages).
     *
     * @public
     * @param value - The value to check.
     * @returns `true` if the value is a valid CSS length with units; otherwise, `false`.
     */
    export function isLength(value: unknown): value is `${number}${CssLengthUnit}` {
        if (typeof value !== 'string') {
            return false;
        }

        if (value.trim() === '') {
            return false;
        }

        const lengthRegex = /^-?\d+(\.\d+)?(em|ex|ch|rem|vh|vw|vmin|vmax|svw|svh|dvw|dvh|lvw|lvh|px|mm|cm|in|pt|pc|mozmm)/;

        return lengthRegex.test(value);
    }

    /**
     * Converts a `CssLength` to a number.
     * Only works for numeric values without units.
     *
     * @public
     * @param value - The `CssLength` value to convert.
     * @returns The numeric value.
     * @throws Error if the value is not a number.
     */
    export function toNumber(value: CssLength): number {
        if (typeof value === 'number') {
            return value;
        }

        if (value === 'auto') {
            throw new Error('Cannot convert "auto" to a number.');
        }

        const match = (/^-?\d*\.?\d+/).exec(value);
        if (!match) {
            throw new Error(`Invalid CSS length: ${value}`);
        }

        return parseFloat(match[0]);
    }

    /**
     * Converts a `CssLength` to a string representation.
     * Numbers are treated as pixels.
     *
     * @public
     * @param value - The `CssLength` value to convert.
     * @param defaultUnit - The default unit to use if the value is a number.
     * @returns The string representation of the `CssLength`.
     */
    export function toString(value?: CssLength, defaultUnit: CssLengthUnit = 'px'): string {
        if (value === undefined || value === null) {
            return '';
        }

        if (typeof value === 'number') {
            return `${value}${defaultUnit}`;
        }

        return value;
    }

    /**
     * Extracts the unit from a CSS length string.
     *
     * @public
     * @param cssLength - The CSS length string to extract the unit from.
     * @returns The unit as a `CssLengthUnit`.
     * @throws Error if the input is not a valid CSS length.
     */
    export function extractUnit(cssLength: `${number}${CssLengthUnit | '%'}`): CssLengthUnit {
        const match = (/[a-z%]+$/i).exec(cssLength);
        const lengt = match ? (match[0] as CssLengthUnit) : null;

        if (lengt === null) {
            throw new Error(`Invalid value: ${cssLength}`);
        }

        return lengt;
    }

    /**
     * Extracts the numeric value from a CSS length string.
     *
     * @public
     * @param cssLength - The CSS length string to extract the value from.
     * @returns The numeric value as a number.
     * @throws Error if the input is not a valid CSS length.
     */
    export function extractValue(cssLength: `${number}${CssLengthUnit | '%'}`): number {
        const match = (/^-?\d*\.?\d+/).exec(cssLength);
        const value = match ? parseFloat(match[0]) : null;

        if (value === null) {
            throw new Error(`Invalid value: ${cssLength}`);
        }

        return value;
    }

    /**
     * Converts pixel values to rem units.
     *
     * @public
     * @param pixel - The pixel value to convert.
     * @returns The converted value in rem units.
     */
    export function pxToRem(pixel: `${number}${Extract<CssLengthUnit, 'px'>}`): string {
        const value = extractValue(pixel);
        const baseSize = 16; // Default font size in pixels
        const sizeInRem = `${value / baseSize}rem`;
        return sizeInRem;
    }

    /**
     * Tries to parse a value into a `CssLength`.
     *
     * @public
     * @param value - The value to parse.
     * @param defaultUnit - The default unit to use if the value is a string without a unit.
     * @returns The parsed `CssLength` or `undefined` if parsing fails.
     */
    export function tryParse(value: unknown, defaultUnit: CssLengthUnit = 'px'): CssLength | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        if (typeof value === 'string') {
            if (isLength(value)) {
                return value;
            }

            if (value.trim() === '') {
                return undefined;
            }

            const num = parseFloat(value);

            return isNaN(num) ? undefined : `${num}${defaultUnit}` as CssLength;
        }

        if (typeof value === 'number') {
            return `${String(value)}${defaultUnit}` as CssLength;
        }

        return undefined;
    }

}
